import { createPhoneSchema } from '@/components/FormFieldPhone';
import { WEBSITE_VALIDATION, ZIP_CODE_SCHEMA } from '@/utils/constant';
import { z } from 'zod';

export const ClientInformationSchema = z
  .object({
    type: z.enum(['INDIVIDUAL', 'FIRM']).optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    address1: z.string().nullable().optional(),
    address2: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z.string().min(2, { message: 'State is invalid' }).nullable().optional(),
    zipCode: ZIP_CODE_SCHEMA.optional().nullish(),
    phone: createPhoneSchema(false),
    fax: createPhoneSchema(false, 'Fax'),
    website: WEBSITE_VALIDATION('Web address is invalid').nullish().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.type) {
      ctx.addIssue({
        message: 'Client type is required',
        path: ['type'],
        code: 'custom',
      });
    }
    if (data.type === 'INDIVIDUAL') {
      if (!data.lastName) {
        ctx.addIssue({
          message: 'Last name is required',
          path: ['lastName'],
          code: 'custom',
        });
      }
      if (!data.firstName) {
        ctx.addIssue({
          message: 'First name is required',
          path: ['firstName'],
          code: 'custom',
        });
      }
    }

    if (data.type === 'FIRM' && !data.clientName) {
      ctx.addIssue({
        message: 'Client name is required',
        path: ['clientName'],
        code: 'custom',
      });
    }

    if (!data.address1) {
      ctx.addIssue({
        message: 'Address line 1 is required',
        path: ['address1'],
        code: 'custom',
      });
    }
    if (!data.city) {
      ctx.addIssue({
        message: 'City is required',
        path: ['city'],
        code: 'custom',
      });
    }
    if (!data.state) {
      ctx.addIssue({
        message: 'State is required',
        path: ['state'],
        code: 'custom',
      });
    }
    if (!data.zipCode) {
      ctx.addIssue({
        message: 'Zip code is required',
        path: ['zipCode'],
        code: 'custom',
      });
    }
  });

export const ClientContactSchema = z.object({
  contacts: z
    .array(
      z
        .object({
          contactId: z.optional(z.string()).nullable(),
          firstName: z.string().optional().nullable(),
          lastName: z.string().optional().nullable(),
          type: z.enum(['LAWYER', 'STAFF']).optional(),
          lawyerBarId: z.optional(z.string()).nullable(),
          email: z
            .string().trim()
            .email({
              message: 'Email address is invalid',
            })
            .optional()
            .nullable(),
          fax: createPhoneSchema(false, 'Fax'),
          cell: createPhoneSchema(false, 'Cell'),
          phone: createPhoneSchema(false),
          extension: z.optional(z.string().regex(/^[a-zA-Z0-9]+$/, { message: 'Extension is invalid' })).nullable(),
        })
        .superRefine((data, ctx) => {
          if (!data.type)
            ctx.addIssue({
              path: ['type'],
              message: 'Employee type is required',
              code: 'custom',
            });
          if (!data.firstName)
            ctx.addIssue({
              path: ['firstName'],
              message: 'First name is required',
              code: 'custom',
            });
          if (!data.lastName)
            ctx.addIssue({
              path: ['lastName'],
              message: 'Last name is required',
              code: 'custom',
            });
          if (!data.email)
            ctx.addIssue({
              path: ['email'],
              message: 'Email address is required',
              code: 'custom',
            });
        })
    ),
});

export const commaSeparatedEmails = z
  .union([z.string(), z.null()])
  .refine(
    (value) => {
      if (value === null || value === undefined || value.length == 0) {
        return true;
      }
      const emails = value.split(',').map((email) => email.trim());
      const emailSchema = z.string().email({ message: 'Email address is invalid' }).trim();
      for (const email of emails) {
        if (!emailSchema.safeParse(email).success) {
          return false;
        }
      }
      return true;
    },
    {
      message: 'One or more email addresses are invalid',
    }
  )
  .refine(
    (value) => {
      if (value === null || value === undefined || value.length == 0) {
        return true;
      }
      const emails = value.split(',').map((email) => email.trim());
      const uniqueEmails = new Set(emails);
      return uniqueEmails.size === emails.length;
    },
    {
      message: 'Duplicate email addresses are not allowed',
    }
  );
export const ClientDataExchangeSchema = z.object({
  affidavit: z.optional(z.array(z.string())).nullable(),
  arInvoices: z.optional(z.array(z.string())).nullable(),
  finalBillingSummary: z.optional(z.array(z.string())).nullable(),
  recapAndInvoice: z.optional(z.array(z.string())).nullable(),
  affidavitWithInvoice: z.optional(z.array(z.string())).nullable(),
  statement: z.optional(z.array(z.string())).nullable(),
  arInvoicesCc: z.optional(commaSeparatedEmails).nullable(),
  finalBillingSummaryCc: z.optional(commaSeparatedEmails).nullable(),
  recapAndInvoiceCc: z.optional(commaSeparatedEmails).nullable(),
  affidavitWithInvoiceCc: z.optional(commaSeparatedEmails).nullable(),
  affidavitCc: z.optional(commaSeparatedEmails).nullable(),
  canAffidavitWithInvoice: z.boolean().optional().nullable(),
  statementCc: z.optional(commaSeparatedEmails).nullable(),
});

export const ClientInstructionSchema = z.object({
  instructions: z.string().optional().nullable(),
});
